import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { type NoteSummary } from '@/api/notes';
import { ChevronRightIcon, FolderIcon, StarIcon } from '@/features/notes/icons';

interface TreeNode extends NoteSummary {
  children: TreeNode[];
}

export function buildTree(flat: NoteSummary[]): TreeNode[] {
  const byId = new Map<string, TreeNode>();
  flat.forEach((n) => byId.set(n.id, { ...n, children: [] }));
  const roots: TreeNode[] = [];
  for (const n of byId.values()) {
    if (n.parent_id && byId.has(n.parent_id)) {
      byId.get(n.parent_id)!.children.push(n);
    } else {
      roots.push(n);
    }
  }
  return roots;
}

interface TreeItemProps {
  node: TreeNode;
  depth: number;
  color: string;
  selectedId: string | null;
  dropTargetId: string | null | 'root';
  draftIds: Set<string>;
  onSelect: (id: string) => void;
  onAddChild: (parentId: string) => void;
  onRename: (id: string, title: string) => void;
  onTogglePin: (id: string, currentlyPinned: boolean) => void;
  onDragStartItem: (id: string, e: React.DragEvent) => void;
  onDragOverItem: (id: string, e: React.DragEvent) => void;
  onDropItem: (id: string, e: React.DragEvent) => void;
}

export function TreeItem({
  node,
  depth,
  color,
  selectedId,
  dropTargetId,
  draftIds,
  onSelect,
  onAddChild,
  onRename,
  onTogglePin,
  onDragStartItem,
  onDragOverItem,
  onDropItem,
}: TreeItemProps) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(true);
  const [editing, setEditing] = useState(false);
  const [draftTitle, setDraftTitle] = useState(node.title);
  const hasChildren = node.children.length > 0;
  const active = node.id === selectedId;
  const isDropTarget = dropTargetId === node.id;
  const hasDraft = draftIds.has(node.id);

  const commit = () => {
    const trimmed = draftTitle.trim();
    if (trimmed && trimmed !== node.title) {
      onRename(node.id, trimmed);
    }
    setEditing(false);
  };
  const cancel = () => {
    setDraftTitle(node.title);
    setEditing(false);
  };

  const rowStyle: React.CSSProperties = {
    paddingLeft: `${8 + depth * 14}px`,
    ...(isDropTarget
      ? { background: 'var(--accent-bg-strong)', boxShadow: 'inset 0 0 0 1px var(--accent)' }
      : active
        ? {
            background: hasDraft ? 'var(--bg-amber)' : 'var(--accent-bg)',
            color: hasDraft ? 'var(--c-amber)' : 'var(--accent)',
          }
        : hasDraft
          ? { color: 'var(--c-amber)' }
          : {}),
  };

  return (
    <li>
      <div
        draggable
        onDragStart={(e) => onDragStartItem(node.id, e)}
        onDragOver={(e) => onDragOverItem(node.id, e)}
        onDrop={(e) => onDropItem(node.id, e)}
        className="group flex items-center gap-1 pr-2 text-sm transition-colors cursor-grab active:cursor-grabbing"
        style={rowStyle}
        onMouseEnter={(e) => {
          if (!isDropTarget && !active) {
            e.currentTarget.style.background = 'var(--bg-hover)';
          }
        }}
        onMouseLeave={(e) => {
          if (!isDropTarget && !active) {
            e.currentTarget.style.background = 'transparent';
          }
        }}
      >
        {hasChildren ? (
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className="w-4 flex-shrink-0 grid place-items-center"
            style={{ color: 'var(--text-dim)' }}
            aria-label={expanded ? t('notes_ui.list.collapse') : t('notes_ui.list.expand')}
          >
            <ChevronRightIcon
              className="w-3 h-3"
              style={{
                transform: expanded ? 'rotate(90deg)' : 'rotate(0deg)',
                transition: 'transform 180ms cubic-bezier(0.2, 0, 0, 1)',
              }}
            />
          </button>
        ) : (
          <span className="w-4 flex-shrink-0" />
        )}
        <FolderIcon
          className="w-3.5 h-3.5 flex-shrink-0"
          style={{ color }}
        />
        {editing ? (
          <input
            autoFocus
            value={draftTitle}
            onChange={(e) => setDraftTitle(e.target.value)}
            onBlur={commit}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                commit();
              } else if (e.key === 'Escape') {
                e.preventDefault();
                cancel();
              }
            }}
            onClick={(e) => e.stopPropagation()}
            className="flex-1 min-w-0 py-1 px-1 text-sm rounded focus:outline-none"
            style={{
              background: 'var(--bg-elevated)',
              border: `1px solid ${color}`,
              color: 'var(--text)',
            }}
          />
        ) : (
          <button
            type="button"
            onClick={() => onSelect(node.id)}
            onDoubleClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setDraftTitle(node.title);
              setEditing(true);
            }}
            className="flex-1 text-left py-1.5 min-w-0 flex items-center gap-2"
            style={{ color: active ? undefined : 'var(--text)' }}
            title={t('notes_ui.list.rename_hint')}
          >
            <span className="truncate">{node.title}</span>
            <span
              className="text-[10px] flex-shrink-0 font-mono"
              style={{ color: 'var(--text-dim)' }}
            >
              {node.char_count}c
            </span>
          </button>
        )}
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onTogglePin(node.id, node.pinned);
          }}
          className={`px-1 transition-opacity ${
            node.pinned ? '' : 'opacity-0 group-hover:opacity-100'
          }`}
          style={{ color: node.pinned ? 'var(--c-amber)' : 'var(--text-dim)' }}
          title={node.pinned ? t('notes.unpin') : t('notes.pin')}
        >
          <StarIcon filled={node.pinned} className="w-3 h-3" />
        </button>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onAddChild(node.id);
          }}
          className="opacity-0 group-hover:opacity-100 text-xs px-1"
          style={{ color: 'var(--text-dim)' }}
          title={t('notes_ui.list.add_subnote')}
        >
          +
        </button>
      </div>
      {hasChildren && (
        <div
          style={{
            maxHeight: expanded ? '5000px' : '0px',
            overflow: 'hidden',
            transition: expanded
              ? 'max-height 280ms cubic-bezier(0.2, 0, 0, 1)'
              : 'max-height 180ms cubic-bezier(0.4, 0, 1, 1)',
          }}
        >
          <ul>
            {node.children.map((child) => (
              <TreeItem
                key={child.id}
                node={child}
                depth={depth + 1}
                color={color}
                selectedId={selectedId}
                dropTargetId={dropTargetId}
                draftIds={draftIds}
                onSelect={onSelect}
                onAddChild={onAddChild}
                onRename={onRename}
                onTogglePin={onTogglePin}
                onDragStartItem={onDragStartItem}
                onDragOverItem={onDragOverItem}
                onDropItem={onDropItem}
              />
            ))}
          </ul>
        </div>
      )}
    </li>
  );
}
